import { createStore, createEffect, createEvent, sample } from 'effector';
import { transformDashboardsThumbnailsUrls } from '../../util/ThumbnailsUtils';
import { ghApi } from '../DataService';
import { addServerUrlToImage } from '../LogoService';
import { BiUserView, DashboardView, SigmaAuthenticationStatus, SigmaInfo, UserDashboardStatus, UserDashboardStatusType } from './types';
import { delay, spread } from 'patronum';
import { createSigmaInfo, RELOAD_SIGMA_INFO_MAX_RETRIES, SIGMA_INFO_AUTHENTICATING, SIGMA_INFO_AUTHENTICATION_FAILED, SIGMA_INFO_NOT_AUTHENTICATED } from './BiUtils';

const RETRY_DELAY = import.meta.env.VITE_RETRY_DELAY_SECONDS * 1000;

//-------------------------------- Effects -------------------------------
const getSigmaInfoFx = createEffect<void, SigmaInfo>(async () => {
  // The Sigma user has to exist before we can get the dashboards for the user. The login operation creates the user if it does not exist.
  console.log(`Logging in to the BI server (Retry count: ${$reloadSigmaInfoRetryCount.getState()})... `);
  const sigmaUserInfo = await performSigmaLogin();
  return createSigmaInfo(SigmaAuthenticationStatus.AUTHENTICATED, sigmaUserInfo.dashboards);
});

//-------------------------------- Events --------------------------------

const reinitAll = createEvent<void>();
const reloadSigmaInfo = createEvent<void>();
const sigmaServerLogout = createEvent<void>();

//-------------------------------- Stores --------------------------------
const $sigmaInfo = createStore<SigmaInfo>(SIGMA_INFO_NOT_AUTHENTICATED).reset(sigmaServerLogout, reinitAll);

const $reloadSigmaInfoRetryCount = createStore(1).reset(getSigmaInfoFx.done, sigmaServerLogout, reinitAll);

const reloadSigmaInfoDelay = delay({
  source: sample({
    source: $reloadSigmaInfoRetryCount,
    // Ignore the reset value so the retries don't start again once $reloadSigmaInfoRetryCount is reset with getSigmaInfoFx.done
    filter: reloadSigmaInfoRetryCount => reloadSigmaInfoRetryCount !== 1
  }),
  timeout: reloadSigmaInfoRetryCount => {
    return (reloadSigmaInfoRetryCount - 1) * RETRY_DELAY;
  }
});

const $sigmaDashboardStatus = sample({
  source: {
    sigmaInfo: $sigmaInfo,
    reloadSigmaInfoRetryCount: $reloadSigmaInfoRetryCount
  },
  fn: ({ sigmaInfo, reloadSigmaInfoRetryCount }): UserDashboardStatusType => {
    if (
      (sigmaInfo?.authenticationStatus === SigmaAuthenticationStatus.NOT_AUTHENTICATED || sigmaInfo?.authenticationStatus === SigmaAuthenticationStatus.AUTHENTICATING) &&
      reloadSigmaInfoRetryCount === 1
    ) {
      return UserDashboardStatus.FIRST_AUTHENTICATION_ATTEMPT;
    } else if (sigmaInfo?.authenticationStatus === SigmaAuthenticationStatus.AUTHENTICATING && reloadSigmaInfoRetryCount <= RELOAD_SIGMA_INFO_MAX_RETRIES + 1) {
      return UserDashboardStatus.RETRYING_AUTHENTICATION;
    } else if (sigmaInfo?.authenticationStatus === SigmaAuthenticationStatus.AUTHENTICATION_FAILED) {
      return UserDashboardStatus.MAX_AUTHENTICATION_RETRIES_REACHED;
    } else if (sigmaInfo?.authenticationStatus === SigmaAuthenticationStatus.AUTHENTICATED && sigmaInfo?.dashboardsNo == 0) {
      // When the user is created in Sigma but is not fully setup (Assigning the user to groups in legacy databases
      // may take some time), the user will be authenticated but won't have access to any dashboard.
      return UserDashboardStatus.USER_NOT_FULLY_SETUP;
    } else if (sigmaInfo?.authenticationStatus === SigmaAuthenticationStatus.AUTHENTICATED) {
      return UserDashboardStatus.USER_AUTHENTICATED_WITH_DASHBOARDS;
    }
    return undefined;
  }
});

//-------------------------------- Samples -------------------------------
sample({
  clock: [reloadSigmaInfo, reloadSigmaInfoDelay],
  target: getSigmaInfoFx
});

sample({
  clock: getSigmaInfoFx.doneData,
  target: $sigmaInfo
});

sample({
  source: $reloadSigmaInfoRetryCount,
  clock: getSigmaInfoFx.fail,
  fn: (reloadSigmaInfoRetryCount, error) => {
    console.error('Failed to load BI server info:', error); // Delete or keep it for troubleshooting

    const result: {
      sigmaInfo?: SigmaInfo;
      reloadSigmaInfoRetryCount?: number;
    } = {};

    if (reloadSigmaInfoRetryCount <= RELOAD_SIGMA_INFO_MAX_RETRIES) {
      // If retry count is within limits, increase the retry counter so the delayed effect is called
      result.reloadSigmaInfoRetryCount = reloadSigmaInfoRetryCount + 1;
      result.sigmaInfo = SIGMA_INFO_AUTHENTICATING;
    } else {
      // Otherwise, set $sigmaInfo to its final failed authentication state
      result.sigmaInfo = SIGMA_INFO_AUTHENTICATION_FAILED;
    }

    return result;
  },
  target: spread({
    sigmaInfo: $sigmaInfo,
    reloadSigmaInfoRetryCount: $reloadSigmaInfoRetryCount
  })
});

//-------------------------------- Private functions -------------------------------

/**
 * Fetches all dashboards for admin user.
 *
 * @returns {Promise<DashboardView[]>} Promise containing all the dashboards for admin user.
 */
export async function getAdminDashboards(): Promise<DashboardView[]> {
  return ghApi
    .get('/bi/admin/dashboards')
    .then(response => response.data)
    .then(transformDashboardsThumbnailsUrls);
}

/**
 * Updates the metadata (thumbnail image, dashboard description) for a specific dashboard.
 *
 * @param {object} params parameters
 * @param {string} params.id - The ID of the dashboard to update.
 * @param {string} params.description - Description of the dashboard.
 * @param {string} params.thumbnailLightBase64 - Base64 string with the thumbnail for the light theme.
 * @param {string} params.thumbnailDarkBase64 - Base64 string with the thumbnail for the dark theme.
 * @returns {Promise<void>} A promise that resolves when the metadata has been updated.
 */
export const updateAdminDashboardMetadata = async ({ id, ...data }: { id: string; description: string; thumbnailLightBase64: File; thumbnailDarkBase64: File }): Promise<void> => {
  return ghApi.post(`/bi/admin/dashboards/${id}`, data).then(response => response.data);
};

export const getEmbedDashboardUrl = async (workbookId: string, bookmarkId: string, theme: string): Promise<string> => {
  return ghApi.get(`/bi/embedUrl/dashboard/${workbookId}`, { params: { bookmarkId, theme } }).then(response => response.data);
};

/**
 * Fetches, from Sigma, the information of the current user, including all dashboards. If the user does not exist, it creates it.
 *
 * @returns {Promise<BiUserView>} Promise containing all the user information.
 */
async function performSigmaLogin(): Promise<BiUserView> {
  return ghApi
    .put('bi/login')
    .then(response => response.data)
    .then(userView => ({
      ...userView,
      dashboards: userView.dashboards.map(dashboard => ({
        ...dashboard,
        thumbnailLight: addServerUrlToImage(dashboard.thumbnailLight),
        thumbnailDark: addServerUrlToImage(dashboard.thumbnailDark)
      }))
    }));
}

export const $$bi = {
  $sigmaInfo,
  $reloadSigmaInfoRetryCount,
  $sigmaDashboardStatus,

  reloadSigmaInfo,
  sigmaServerLogout,
  getSigmaInfoFx,

  __: {
    reinitAll
  }
};
